import { useEffect, useState } from "react";
import { Alert, Button, Col, FormControl, HelpBlock, Row, Well } from "react-bootstrap";
import { toast } from "react-toastify";
import type { IDonorMentorCannedResponse, IDonorMentorSendEmailRequest } from "@nkr/models/dash/donor-mentor-canned-response";
import { useDonorMentorCannedResponseSenderApi } from "@nkr/models/dash/donor-mentor-canned-response";
import { useForm } from "@nkr/utils/rest-api";

const emailPattern = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const EMPTY_TEMPLATE_ID = "";

const splitRecipients = (value: string) => value
    .split(/[\n,;]+/)
    .map(x => x.trim())
    .filter(Boolean);

const resolveTemplateVariables = (value: string, donorFirstName: string, mentorFullName: string) =>
    value
        .replace("{CandidateFirstName}", donorFirstName ?? "")
        .replace("{UserFullName}", mentorFullName ?? "");

const EMPTY_CANNED_RESPONSE: IDonorMentorCannedResponse = {
    id: 0,
    name: "",
    emailSubject: "",
    emailContent: "",
};

interface IProps {
    registrationId: number,
    donorEmail: string,
    donorFirstName: string,
    mentorFullName: string,
    canSendMentorEmail: boolean,
    mentorEmailBlockedReason?: string,
    onClose: () => void,
    onSent: () => void,
}

const DonorMentorSendEmail = (props: IProps) => {
    const [selectedEmail, setSelectedEmail] = useState<IDonorMentorCannedResponse>(EMPTY_CANNED_RESPONSE);
    const [recipientInput, setRecipientInput] = useState(props.donorEmail ?? "");
    const [saving, setSaving] = useState(false);
    const api = useDonorMentorCannedResponseSenderApi(props.registrationId);
    const results = api.useGetAll();
    const createHook = api.useCreate();
    const form = useForm<IDonorMentorCannedResponse>(api, null, null, null, selectedEmail);

    useEffect(() => {
        setRecipientInput(props.donorEmail ?? "");
    }, [props.donorEmail]);

    const recipients = splitRecipients(recipientInput);
    const hasInvalidEmails = recipients.some(x => !emailPattern.test(x));
    const isValid = recipients.length > 0
        && !hasInvalidEmails
        && form.data.emailSubject?.trim() !== ""
        && form.data.emailContent?.trim() !== "";

    const handleTemplateChange = (value: string) => {
        const selectedTemplate = results.payload?.find(item => item.id.toString() === value);
        const nextValue = selectedTemplate
            ? {
                ...selectedTemplate,
                emailSubject: resolveTemplateVariables(selectedTemplate.emailSubject ?? "", props.donorFirstName, props.mentorFullName),
                emailContent: resolveTemplateVariables(selectedTemplate.emailContent ?? "", props.donorFirstName, props.mentorFullName),
            }
            : EMPTY_CANNED_RESPONSE;

        setSelectedEmail(nextValue);
        form.replaceData(nextValue, { baseline: false, dirty: true });
    };

    const handleSend = async () => {
        if (!isValid || !props.canSendMentorEmail) {
            return;
        }

        setSaving(true);
        const payload = {
            ...form.data,
            recipients,
        } as IDonorMentorCannedResponse & IDonorMentorSendEmailRequest;
        const response = await createHook.submit(payload as IDonorMentorCannedResponse);

        setSaving(false);

        if (response?.isSuccess) {
            props.onClose();
            toast.success("Email Sent Successfully", {
                onClose: () => props.onSent(),
            });

            clearEmailForm();
            
            return;
        }

        toast.error(response?.message || "Unable to send donor mentor email");
    };

    const clearEmailForm = () => {
        setSelectedEmail(EMPTY_CANNED_RESPONSE);
        
        form.replaceData(EMPTY_CANNED_RESPONSE, { baseline: false, dirty: true });
    };

    return (
        <>
            <Row style={{ marginBottom: 10 }}>
                <Col xs={8}><strong>Send Donor Mentor Email</strong></Col>
            </Row>

            {!props.canSendMentorEmail && (
                <Alert bsStyle="warning">
                    {props.mentorEmailBlockedReason || "This donor is not eligible for donor mentor email communication."}
                </Alert>
            )}

            <Row style={{ marginBottom: 10 }}>
                <Col xs={12}><strong>Template</strong></Col>
                <Col xs={12}>
                    <FormControl componentClass="select" value={selectedEmail.id || EMPTY_TEMPLATE_ID} onChange={e => handleTemplateChange((e.target as HTMLSelectElement).value)}>
                        <option value={EMPTY_TEMPLATE_ID}>-- Select an email --</option>
                        {results.isSuccess && results.payload?.map(item => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                    </FormControl>
                </Col>
            </Row>

            <Row style={{ marginBottom: 10 }}>
                <Col xs={12}><strong>Recipient</strong></Col>
                <Col xs={12}>
                    <FormControl
                        value={recipientInput}
                        disabled={true}
                        onChange={e => setRecipientInput((e.target as HTMLTextAreaElement).value)}
                    />
                    {hasInvalidEmails && (
                        <HelpBlock style={{ color: "#a94442" }}>One or more recipient email addresses are invalid.</HelpBlock>
                    )}
                </Col>
            </Row>

            <Row style={{ marginBottom: 10 }}>
                <Col xs={12}><strong>Email Subject</strong></Col>
                <Col xs={12}>
                    <FormControl
                        name="emailSubject"
                        value={form.data.emailSubject ?? ""}
                        onChange={form.handleStringChange}
                    />
                </Col>
            </Row>

            <Row style={{ marginBottom: 10 }}>
                <Col xs={12}><strong>Email Content</strong></Col>
                <Col xs={12}>
                    <FormControl
                        componentClass="textarea"
                        name="emailContent"
                        value={form.data.emailContent ?? ""}
                        onChange={form.handleStringChange}
                        style={{ height: 220 }}
                    />
                    <HelpBlock>Links use markdown syntax: [Link text](https://example.com)</HelpBlock>
                </Col>
            </Row>

            <Button bsStyle="primary" disabled={!isValid || !props.canSendMentorEmail || saving} onClick={handleSend}>
                {saving ? "Sending..." : "Send Email"}
            </Button>
        </>
    );
};

export default DonorMentorSendEmail;